import { UserID } from "../../user/user.interface"
import { TripID } from "../../trips/trip.interface"
import {
  CabinPaymentLineItemMeta,
  PaymentLineItem,
  PaymentLineItemPayload,
} from "./payment-line-item.types"
import { TripBookingID } from "../../booking/trip-booking.interface"

export enum PaymentPlanEnum {
  Total = "total",
  TotalSplit = "totalSplit",
  DepositOnly = "depositOnly",
  DepositWithAutoFinalPayment = "depositWithAutoFinalPayment",
}

export type PaymentPlan = `${PaymentPlanEnum}` | PaymentPlanEnum

export type PaymentRequestID = string

export enum PaymentRequestStatusEnum {
  RequiresPaymentMethod = "requiresPaymentMethod",
  Approved = "approved",
  PartiallyPaid = "partiallyPaid",
  TotalPaid = "totalPaid",
  RejectedByUser = "rejectedByUser",
  CancelledByAgent = "cancelledByAgent",
  Confirmed = "confirmed", // When advisor books a cabin for room with payment request
}

export type PaymentRequestStatus = `${PaymentRequestStatusEnum}` | PaymentRequestStatusEnum

export type PaymentRequestMeta = {
  isDemo?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export type PaymentRequest<TMeta = PaymentRequestMeta> = {
  id: PaymentRequestID

  status: PaymentRequestStatus

  tripId: TripID

  /**
   * Represents the unique identifier for the sender of a payment request request (e.g., travel advisor).
   */
  senderId: UserID

  /**
   * Represents an optional code assigned to an agent.
   * This code may be used to uniquely identify an agent within a system.
   */
  agentCode?: string

  /**
   * Represents the list of assigned users to provide payment method details: bank account or credit card
   * (e.g., room guest that would pay for cabin booking).
   */
  assignedTo: UserID[]

  /**
   * Represents the currency code in ISO 4217 format.
   * This variable holds a three-letter code used to define the name of the currency.
   * (e.g., USD)
   */
  currency: string

  /**
   * Represents the amount without taxes and fees as a string.
   */
  amount: string

  /**
   * Represents the taxes and fees as a string.
   */
  taxesAndFees?: string

  /**
   * Represents the total amount as a string.
   */
  total: string

  label?: string
  description?: string
  termsAndConditions?: string

  /**
   * Represents an optional array of payment line items associated with a payment request.
   *
   * This variable holds detailed information about various items that are part of
   * a payment process. Each element in the array is an instance of `PaymentLineItem`,
   * which contains specific details like description, amount, and quantity.
   */
  lineItems: PaymentLineItem[]

  bookingId?: TripBookingID

  created: Date
  createdBy: UserID

  updated: Date
  updatedBy: UserID

  meta: TMeta
}

export type PaymentRequestPayload = Pick<
  PaymentRequest,
  "tripId" | "assignedTo" | "label" | "description" | "termsAndConditions"
> & {
  lineItems: PaymentLineItemPayload<CabinPaymentLineItemMeta>[]
  meta?: PaymentRequestMeta
}
